import React, { useEffect, useCallback, useRef, useState } from 'react'
import { isMobile } from 'lib/device'
import cn from 'classnames'
import SearchHeader from './SearchHeader'
import PanelLoadingIndicator from './PanelLoadingIndicator'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useOpenRight from 'refactor/hooks/useOpenRight'
import {
    useGetMemes,
    MemeHub,
    MemeParticipant,
    MemePreviewMedia,
} from 'refactor/hooks/api/useGetMemes'
import { memeOrderParams } from 'lib/memeSort'
import { recordEngagementOpen, useQuestImpression } from 'lib/questImpressions'
import { useInView } from 'react-intersection-observer'
import StaticAnswerBody from 'components/answers/StaticAnswerBody'
import InitialsAvatar from 'components/users/InitialsAvatar'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import { getAnswerPermalinkQuestKey } from 'utils/shareUtils'
import { memeHubPath, questAnswerPath, questPath } from 'lib/seoPaths'
import { initialFeedPageFromUrl, feedPageHref } from 'lib/feedPagination'
import FeedNextPageLink from 'components/shared/FeedNextPageLink'
import { Bitcoin, Play, Zap } from 'lucide-react'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import {
    formatMemeAge,
    formatMemeThreadCount,
    MemeValuePresentation,
    resolveMemeCardPreview,
    resolveMemeValuePresentation,
} from './memeCardPresentation'
import styles from './memes.module.scss'
import panelStyles from './panel.module.scss'
import { Filter } from 'components/filters/useStreamFilters'

interface MemesProps {
    filter?: Filter
    hideSearchHeader?: boolean
}

function Loading() {
    return <PanelLoadingIndicator label="Loading memes" />
}

function MemeAvatar({ user, className }: { user?: MemeParticipant | null; className: string }) {
    const avatarUrl = user?.avatar_urls?.small || user?.avatar_urls?.original

    return (
        <span
            aria-hidden="true"
            className={cn(className, !avatarUrl && !user?.name && styles.avatarPlaceholder)}
        >
            {avatarUrl ? (
                <img src={avatarUrl} alt="" />
            ) : user ? (
                <InitialsAvatar user={user} />
            ) : null}
        </span>
    )
}

function MemeValue({
    value,
    compact = false,
}: {
    value: MemeValuePresentation
    compact?: boolean
}) {
    return (
        <span
            className={cn(styles.valueAmount, styles[value.kind], compact && styles.compactValue)}
        >
            {value.kind === 'sats' && <Zap className={styles.valueIcon} fill="currentColor" />}
            {value.kind === 'bitcoin' && <Bitcoin className={styles.bitcoinIcon} />}
            <span>{value.amount}</span>
        </span>
    )
}

function PreviewMedia({ media }: { media?: MemePreviewMedia | null }) {
    const imageUrl = media?.kind === 'video' ? media.poster_url : media?.url
    if (!media || !imageUrl) return null

    return (
        <div className={styles.previewMedia} aria-hidden="true">
            <img
                className={styles.previewMediaAsset}
                src={imageUrl}
                alt=""
                width={media.width || undefined}
                height={media.height || undefined}
                loading="lazy"
            />
            {media.kind === 'video' && (
                <span className={styles.previewVideoBadge}>
                    <Play className={styles.previewVideoIcon} fill="currentColor" />
                    {media.duration && <span>{media.duration}</span>}
                </span>
            )}
        </div>
    )
}

function shouldUseNativeAnchorNavigation(ev: React.MouseEvent): boolean {
    return ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey
}

function MemeCard({
    meme,
    position,
    personal,
}: {
    meme: MemeHub
    position: number
    personal: boolean
}) {
    const { panel, scrollContainerRef, hide, filter } = usePanelContext()
    const { openRight } = useOpenRight(panel?.panelId)
    const { useUsd, formatSatsUsd } = useBsvDisplay()
    const cardRef = useRef<HTMLDivElement>(null)
    const { ref: nearViewRef, inView: nearView } = useInView({
        root: scrollContainerRef.current,
        rootMargin: '500px 0px 500px 0px',
        threshold: 0,
        skip: !!hide,
    })
    const setCardRef = useCallback(
        (node: HTMLDivElement | null) => {
            cardRef.current = node
            nearViewRef(node)
        },
        [nearViewRef],
    )

    // Top New for meme hubs reuses the representative thread's global seen
    // state, just like the pair-based Ordinals stream.
    useQuestImpression({
        elementRef: cardRef,
        questId: meme.top_thread?.id,
        spaceId: meme.top_thread?.space_id,
        position,
        filter,
        rootRef: scrollContainerRef,
        // The hub is an unbounded, non-virtualized list. Keep the qualifying
        // observer only around the shared near-viewport window.
        disabled: !!hide || !nearView,
    })

    const onOpenMeme = (ev: React.MouseEvent<HTMLAnchorElement>) => {
        // The hub link does not carry a questId through openRightCore, but the
        // card exposure is keyed by its representative thread. Record this
        // primary click explicitly (including native new-tab navigation).
        recordEngagementOpen(meme.top_thread?.id, {
            filter,
            spaceId: meme.top_thread?.space_id,
        })
        if (shouldUseNativeAnchorNavigation(ev)) {
            ev.stopPropagation()
            return
        }
        ev.preventDefault()
        ev.stopPropagation()
        window.logEv?.('CLICK_MEME_HUB', { meme_text: meme.text, from_location: 'memes_panel' })
        // Same filter shape the /meme/:memeText route loads.
        openRight({ meme: meme.text, all: true })
    }

    const {
        answer: previewAnswer,
        media: previewMedia,
        upvalueTotalSats: previewUpvalueTotalSats,
    } = resolveMemeCardPreview(meme.top_thread)
    const previewValue =
        previewAnswer?.delta_json ||
        previewAnswer?.nested_delta_json ||
        (previewAnswer?.content ? { ops: [{ insert: previewAnswer.content }] } : undefined)
    const threadQuestHref =
        meme.top_thread?.path ||
        questPath(meme.top_thread?.id || getAnswerPermalinkQuestKey(previewAnswer))
    const threadHref =
        threadQuestHref && previewAnswer?.id && previewAnswer.id !== meme.top_thread?.parent?.id
            ? questAnswerPath(threadQuestHref, previewAnswer.id)
            : threadQuestHref
    const onOpenThread = async (ev: React.MouseEvent<HTMLElement>) => {
        const questId = meme.top_thread?.id || getAnswerPermalinkQuestKey(previewAnswer)
        recordEngagementOpen(questId, { filter, spaceId: meme.top_thread?.space_id })
        if (shouldUseNativeAnchorNavigation(ev)) {
            ev.stopPropagation()
            return
        }
        ev.preventDefault()
        ev.stopPropagation()
        if (!previewAnswer?.id) return

        if (questId) await openRight({ questId, answerId: previewAnswer.id })
    }

    const lastActivity = formatMemeAge(meme.last_activity_at, Date.now(), true)
    const previewAge = formatMemeAge(previewAnswer?.created_at)
    const hubValue = resolveMemeValuePresentation(meme.upvalue_total_sats, useUsd, formatSatsUsd)
    const previewAnswerValue = resolveMemeValuePresentation(
        previewUpvalueTotalSats,
        useUsd,
        formatSatsUsd,
    )
    const usageCount = personal ? meme.usage_count ?? meme.thread_count : meme.thread_count
    const metaFacts = [
        meme.recent_thread_count ? (
            <span className={styles.memeHotCount} key="hot">
                {meme.recent_thread_count} this week
            </span>
        ) : null,
        <span key="count">{formatMemeThreadCount(usageCount)}</span>,
        lastActivity ? <span key="activity">{lastActivity}</span> : null,
    ].filter(Boolean)
    const participants = (meme.participants || []).slice(0, 4)
    const participantCount = meme.participant_count ?? participants.length
    const participantOverflow = Math.max(0, participantCount - participants.length)
    const replyCount = meme.top_thread?.reply_count

    return (
        <div ref={setCardRef} className={styles.memeCard}>
            <a
                className={styles.memeHubLink}
                href={meme.path || memeHubPath(meme.text)}
                onClick={onOpenMeme}
            >
                <div className={styles.memeCardHead}>
                    <div className={styles.memeLabel}>
                        <span aria-hidden="true" className={styles.memeGlyph}>
                            [[ ]]
                        </span>
                        <span className={styles.memeText}>{meme.text}</span>
                    </div>
                    {hubValue && (
                        <span
                            className={styles.memeTotal}
                            title="Total upvalue across this meme's threads"
                        >
                            <MemeValue value={hubValue} />
                            <span className={styles.valueCaption}>{hubValue.caption}</span>
                        </span>
                    )}
                </div>

                <div className={styles.memeMeta}>
                    <span className={styles.memeMetaFacts}>
                        {metaFacts.map((fact, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <span className={styles.metaSeparator}>·</span>}
                                {fact}
                            </React.Fragment>
                        ))}
                    </span>

                    {participants.length > 0 && (
                        <span
                            aria-label={`${participantCount} ${
                                participantCount === 1 ? 'participant' : 'participants'
                            }`}
                            className={styles.participants}
                            role="img"
                        >
                            <span className={styles.participantAvatars}>
                                {participants.map(participant => (
                                    <MemeAvatar
                                        key={participant.id}
                                        user={participant}
                                        className={styles.participantAvatar}
                                    />
                                ))}
                            </span>
                            {participantOverflow > 0 && (
                                <span className={styles.participantOverflow}>
                                    +{participantOverflow}
                                </span>
                            )}
                        </span>
                    )}
                </div>
            </a>

            {previewAnswer && (
                <ErrorBoundary label="meme-card-preview">
                    <div className={styles.threadPreview} onClick={onOpenThread}>
                        <a
                            className={styles.threadPreviewLink}
                            href={threadHref}
                            onClick={onOpenThread}
                        >
                            Top thread
                        </a>
                        <div className={styles.previewByline}>
                            <MemeAvatar
                                user={previewAnswer.user}
                                className={styles.previewAvatar}
                            />
                            {previewAnswer.user?.name && (
                                <span className={styles.previewAuthor}>
                                    {previewAnswer.user.name}
                                </span>
                            )}
                            {previewAge && <span className={styles.previewTime}>{previewAge}</span>}

                            {(replyCount != null || previewAnswerValue) && (
                                <span className={styles.previewStats}>
                                    {replyCount != null && (
                                        <span>
                                            {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                                        </span>
                                    )}
                                    {replyCount != null && previewAnswerValue && (
                                        <span className={styles.previewStatSeparator}>·</span>
                                    )}
                                    {previewAnswerValue && (
                                        <MemeValue value={previewAnswerValue} compact />
                                    )}
                                </span>
                            )}
                        </div>

                        <div className={styles.previewBody}>
                            <StaticAnswerBody
                                value={previewValue}
                                answer={previewAnswer}
                                type="answer"
                                isEmbed
                                plainMemeLinks
                            />
                        </div>
                        <PreviewMedia media={previewMedia} />
                    </div>
                </ErrorBoundary>
            )}
        </div>
    )
}

const Memes: React.FC<MemesProps> = ({ filter, hideSearchHeader = false }) => {
    const { scrollContainerRef, setIsRefreshing, isRefreshing } = usePanelContext()
    const [isLocalRefreshing, setLocalIsRefreshing] = useState(false)
    // Sort lives on filter.order like every other feed; StreamOrder in the
    // panel header writes it. Map the shared order key to the hub-ranking param.
    const { sort, topWindow } = memeOrderParams(filter?.order, filter?.top_window)
    const personal = filter?.memes_scope === 'mine'

    // Use the shared refresh state if available, otherwise fallback to local state
    const handleSetIsRefreshing = useCallback(
        (value: boolean) => {
            if (setIsRefreshing) {
                setIsRefreshing(value)
            } else {
                setLocalIsRefreshing(value)
            }
        },
        [setIsRefreshing],
    )

    // Linked pagination: /memes is a real paginated URL space for crawlers
    // (personal scope stays un-linked — it is per-viewer content).
    const feedPath = personal ? undefined : '/memes'
    const initialPage = initialFeedPageFromUrl(feedPath)

    const {
        memes,
        fetchNextPage,
        hasNextPage,
        nextPage,
        isFetching,
        isFetchingNextPage,
        isLoading,
        refetch,
    } = useGetMemes(sort, {
        embed: true,
        scope: personal ? 'mine' : undefined,
        topWindow,
        initialPage,
    })

    // Handle scroll for infinite loading
    useEffect(() => {
        const scrollContainer = scrollContainerRef?.current
        if (!scrollContainer) return

        const onScroll = () => {
            if (scrollContainer) {
                const { scrollTop, scrollHeight, clientHeight } = scrollContainer
                if (
                    scrollTop + clientHeight >= scrollHeight - 500 &&
                    hasNextPage &&
                    !isFetchingNextPage
                ) {
                    fetchNextPage()
                }
            }
        }

        scrollContainer.addEventListener('scroll', onScroll)

        return () => {
            scrollContainer.removeEventListener('scroll', onScroll)
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage, scrollContainerRef])

    // Handle pull-to-refresh
    useEffect(() => {
        if (isRefreshing || (setIsRefreshing && isLocalRefreshing)) {
            const startTime = Date.now()
            refetch().then(() => {
                const elapsed = Date.now() - startTime
                const remainingTime = Math.max(0, 800 - elapsed) // Minimum 800ms refresh to match other panels

                setTimeout(() => {
                    handleSetIsRefreshing(false)
                }, remainingTime)
            })
        }
    }, [isRefreshing, isLocalRefreshing, refetch, handleSetIsRefreshing])

    return (
        <div className={panelStyles.questStreamComp}>
            {!hideSearchHeader && (
                <div
                    className={cn(
                        panelStyles.searchHeaderContainer,
                        isMobile && 'full-bleed-quests',
                    )}
                >
                    <SearchHeader filter={filter} setIsRefreshing={handleSetIsRefreshing} />
                </div>
            )}

            {isLoading || (isFetching && !memes.length && !isFetchingNextPage) ? (
                <Loading />
            ) : memes.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>
                        {personal
                            ? "You haven't written any memes yet"
                            : sort === 'top'
                            ? 'No memes found'
                            : 'No meme activity in this window yet'}
                    </p>
                </div>
            ) : (
                <div className={cn(styles.memesContainer, isMobile && 'full-bleed-quests')}>
                    {memes.map((meme, ix) => (
                        <MemeCard key={meme.id} meme={meme} position={ix} personal={personal} />
                    ))}
                    {feedPath && hasNextPage && nextPage != null && (
                        <FeedNextPageLink
                            href={feedPageHref(feedPath, nextPage)}
                            onLoadMore={fetchNextPage}
                            isLoading={isFetchingNextPage}
                        />
                    )}
                    {isFetchingNextPage && (
                        <div className={panelStyles.loader}>
                            <div className={cn(panelStyles.loadingIcon)}>
                                <i className="fa fa-circle-o-notch fa-spin fa-lg" />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default Memes
